import { useAppDispatch } from "@/services/hooks";
import classnames from "classnames";
// reactstrap components
import { Button, Col, Label, FormGroup, InputGroup, InputGroupText, Form, Input, Row, } from "reactstrap";
// react plugin for creating notifications over the dashboard
import { ToastContainer } from "react-toastify";
import React, { useState } from "react";
import {
  addDesign,
  updateDesign,
  restoreDesign,
  releaseDesign,
} from "@/services/designServices/designAction";
import NotificationAlertOptions from "@/layouts/Alerts";
import { asCaught } from "@/services/caughtError";

const designForm = {
  name: "",
  source: "",
};
const designChecker = {
  name: false,
  source: false,
};

/**
 * Shared create/update form for GlobalTools designs (SCRUM-903).
 *
 * AddDesign and UpdateDesign were a ~93%-identical twin; this is the merged form.
 * `mode` selects the per-mode behaviour that used to differ (all of it intentional
 * divergence — the SCRUM-903 audit found no drift between the two legs):
 * - "add": starts empty, POSTs via addDesign, resets the form after a successful add.
 * - "update": starts from props.design, PUTs via updateDesign, navigates back via
 *   props.switchToDesignListHandler() after a successful save.
 */
type DesignFormProps = {
  mode: "add" | "update";
  // a design document of the designs slice, which is untyped JS
  design?: any;
  switchToDesignListHandler?: () => void;
};

const DesignForm = (props: DesignFormProps) => {
  const isAdd = props.mode === "add";
  const dispatch = useAppDispatch();
  // SCRUM-1225: a design fetched from a database that has not been migrated
  // still carries `settings_variables`. Drop it here rather than letting it
  // ride along in the update payload, so the form only ever submits fields it
  // actually manages. The server ignores it either way.
  const [design, setDesign] = useState(() => {
    if (isAdd) return designForm;
    const rest = { ...(props.design || {}) };
    delete rest.settings_variables;
    return rest;
  });
  const [checker, setChecker] = useState(designChecker);
  const [showCheckerResult, setShowCheckerResult] = useState(false);
  const [deletedHolder, setDeletedHolder] = useState<{
    id: string;
    msg: string;
    value: string;
  } | null>(null);

  const saveDesign = (data) =>
    isAdd ? dispatch(addDesign(data)) : dispatch(updateDesign(data));
  const failureText = isAdd
    ? "Adding the design failed. Please try again."
    : "Updating the design failed. Please try again.";
  const notify = (type, d) =>
    NotificationAlertOptions(
      type,
      isAdd
        ? `Design ${d.name} is added successfully`
        : `Design ${d.name} has been updated successfully`
    );

  const onSubmit = async (e) => {
    e.preventDefault();

    let formValidatorResponse = await validInputsChecker();
    if (formValidatorResponse) {
      await submit();
    } else {
      setShowCheckerResult(true);
    }
  };

  const submit = async () => {
    // Only confirm / navigate when the request actually succeeds. addDesign /
    // updateDesign re-throw on failure (SCRUM-815), so a failed save no longer
    // shows a success toast / resets / navigates.
    setDeletedHolder(null);
    try {
      await saveDesign(design);
      notify("success", design);
      await setShowCheckerResult(false);
      if (isAdd) {
        await setDesign(designForm);
      } else {
        await props.switchToDesignListHandler?.();
      }
    } catch (err) {
      NotificationAlertOptions(
        "error",
        asCaught(err)?.response?.data?.message || failureText
      );
      // SCRUM-1814: the 409 names the soft-deleted design that still holds this
      // name or source. Offer the two ways out instead of a dead end.
      const first = asCaught(err)?.response?.data?.errors?.[0];
      if (isAdd && first?.deletedId) {
        setDeletedHolder({
          id: String(first.deletedId),
          msg: String(first.msg ?? "A deleted design holds this value"),
          value: String(first.value ?? ""),
        });
      }
    }
  };

  const onRestore = async () => {
    if (!deletedHolder) return;
    try {
      await dispatch(restoreDesign(deletedHolder.id));
      NotificationAlertOptions("success", "The deleted design is restored");
      setDeletedHolder(null);
      setDesign(designForm);
      await props.switchToDesignListHandler?.();
    } catch {
      /* the error is surfaced via GET_ERRORS; the offer stays on screen */
    }
  };

  const onStartFresh = async () => {
    if (!deletedHolder) return;
    try {
      await dispatch(releaseDesign(deletedHolder.id));
    } catch {
      /* the error is surfaced via GET_ERRORS; the offer stays on screen */
      return;
    }
    await submit();
  };

  // SCRUM-1225: this used to also require SettingsRequired to be non-empty, so
  // a design could not be saved without at least one design-level settings
  // variable. That gate went with the fields it guarded; leaving it would have
  // made the form unsubmittable rather than merely showing a stale error.
  const validInputsChecker = () => {
    const checkerDefault = {
      name: false,
      source: false,
    };
    Object.keys(design).map((key) => {
      if (design[key] === "") {
        checkerDefault[key] = true;
      }
    });
    setChecker(checkerDefault);
    return Object.keys(checkerDefault).every((k) => !checkerDefault[k]);
  };

  return (
    <>
      <div className="rna-wrapper">
      <ToastContainer />
      </div>
      <Form role="form" onSubmit={onSubmit}>
        <Row>
          <FormGroup className="col-md-6">
            <Row>
              <Label
                className="form-control-label"
                htmlFor="example-text-input"
                md="2"
              >
                Name
              </Label>
              <Col md="10">
                <InputGroup className={classnames("input-group-merge")}>

                    <InputGroupText>
                      <i className="ni ni-album-2" />
                    </InputGroupText>
                  <Input
                    placeholder="Insert Design Name"
                    name="name"
                    type="text"
                    value={design.name}
                    onChange={(e) =>
                      setDesign({ ...design, name: e.target.value })
                    }
                  />
                </InputGroup>
                {showCheckerResult && checker.name ? (
                  <div className="text-danger"> Please insert design name </div>
                ) : null}
                {showCheckerResult && !checker.name ? (
                  <div className="text-success">Looks good!</div>
                ) : null}
              </Col>
            </Row>
          </FormGroup>
          <FormGroup className="col-md-6">
            <Row>
              <Label
                className="form-control-label"
                htmlFor="example-text-input"
                md="2"
              >
                Source
              </Label>
              <Col md="10">
                <InputGroup className={classnames("input-group-merge")}>

                    <InputGroupText>
                      <i className="ni ni-folder-17" />
                    </InputGroupText>
                  <Input
                    placeholder="Insert Design Source"
                    name="source"
                    type="text"
                    value={design.source}
                    onChange={(e) =>
                      setDesign({ ...design, source: e.target.value })
                    }
                  />
                </InputGroup>
                {showCheckerResult && checker.source ? (
                  <div className="text-danger">
                    {" "}
                    Please insert design source{" "}
                  </div>
                ) : null}
                {showCheckerResult && !checker.source ? (
                  <div className="text-success">Looks good!</div>
                ) : null}
              </Col>
            </Row>
          </FormGroup>
        </Row>
        {/*
          SCRUM-1225: the "Select settings Variables" required/optional pickers
          stood here. Design-level settings were never read by any renderer, so
          anything typed here changed nothing on a site. Settings live on the
          TEMPLATE — Global Tools -> Templates -> Update Template — where a
          value is stored per site. See Design_System.md.
        */}
        {deletedHolder && (
          <div className="alert alert-warning text-left" role="alert">
            {deletedHolder.msg}
            {deletedHolder.value ? (
              <>
                {" "}
                (<strong>{deletedHolder.value}</strong>)
              </>
            ) : null}
            . Restore brings that design back; Start fresh keeps it deleted and
            frees the name and source for this new design.
            <div className="mt-2">
              <button
                type="button"
                className="btn btn-sm btn-warning mr-2"
                onClick={onRestore}
              >
                Restore
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                onClick={onStartFresh}
              >
                Start fresh
              </button>
            </div>
          </div>
        )}
        <Button
          className="btn btn-outline-primary mr-1 float-right"
          type="submit"
        >
          Save Design
        </Button>
      </Form>
    </>
  );
};

export default DesignForm;
